import { useEffect, useState } from "react";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";

// Simple module-level cache so collections are fetched once per page load.
let cachedCollections = null;
let cachedError = null;
let fetching = null;

function normalizeCollections(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((c) => ({
    id: c.id ?? c._id,
    name: c.name ?? "",
    description: c.description ?? "",
    thumbnail: c.thumbnail ?? c.image ?? null,
  }));
}

export function useCollections() {
  const [collections, setCollectionsState] = useState(cachedCollections || []);
  const [loading, setLoading] = useState(!cachedCollections && !cachedError);
  const [error, setError] = useState(cachedError);

  const doFetch = async (force = false) => {
    // If we already have cache (or an error) and not forcing, hydrate from it.
    if (!force && (cachedCollections || cachedError)) {
      setCollectionsState(cachedCollections || []);
      setError(cachedError);
      setLoading(false);
      return;
    }

    // If user is not authenticated, don't try to fetch.
    const token =
      typeof window !== "undefined" ? localStorage.getItem("Token") : null;
    if (!token) {
      cachedCollections = [];
      cachedError = null;
      setCollectionsState([]);
      setError(null);
      setLoading(false);
      return;
    }

    // If a fetch is already in progress, reuse its promise.
    if (!fetching) {
      fetching = axiosInstanceAuth
        .get("/collection")
        .then((res) => {
          const raw = res?.data?.data ?? res?.data;
          const arr = normalizeCollections(raw);
          cachedCollections = arr;
          cachedError = null;
        })
        .catch((e) => {
          cachedCollections = [];
          cachedError = e;
        })
        .finally(() => {
          fetching = null;
        });
    }

    setLoading(true);
    try {
      await fetching;
      setCollectionsState(cachedCollections || []);
      setError(cachedError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    doFetch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCollections = (updater) => {
    setCollectionsState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater || [];
      cachedCollections = next;
      cachedError = null;
      return next;
    });
  };

  const refetch = () => doFetch(true);

  return { collections, loading, error, setCollections, refetch };
}

